import { Header } from "@/app/layout/Header";
import { Main } from "@/app/layout/Main";
import { useActionPermission } from "@/modules/auth/presentation/model/useActionPermission";
import {
  UpdateRoomDisplayScreenSchema,
  type UpdateRoomDisplayScreenDTO,
} from "@/modules/room-displays/domain/contracts/room-displays";
import { Breadcrumb } from "@/shared/ui/Breadcrumb";
import {
  Badge,
  Button,
  confirm,
  FormCheckbox,
  FormInput,
  FormRoot,
  Input,
  RHF,
  toast,
  zodResolver,
} from "@devhop/ui";
import { useNavigate, useParams } from "@tanstack/react-router";
import {
  ArrowDownIcon,
  ArrowUpIcon,
  ExternalLinkIcon,
  Trash2Icon,
} from "lucide-react";
import { useEffect, useState } from "react";
import {
  useCreatePoster,
  useDeletePoster,
  useReorderPosters,
  useRoomDisplayQuery,
  useUpdatePoster,
  useUpdateRoomDisplay,
} from "../api/queries";
import { EditPosterDurationDialog } from "../ui/EditPosterDurationDialog";
import { RoomDisplayImageUpload } from "../ui/RoomDisplayImageUpload";
import { RoomDisplayVideoUpload } from "../ui/RoomDisplayVideoUpload";

type PendingPoster =
  | { kind: "image"; key: string; durationSec: number }
  | { kind: "video"; key: string; videoDurationSec: number };

export function RoomDisplayDetailPage() {
  const { id } = useParams({ from: "/app/room-displays/$id" });
  const nav = useNavigate({ from: "/app/room-displays/$id" });
  const canUpdate = useActionPermission("room-displays:update");
  const detail = useRoomDisplayQuery(id);
  const updateScreen = useUpdateRoomDisplay(id);
  const createPoster = useCreatePoster(id);
  const updatePoster = useUpdatePoster(id);
  const deletePoster = useDeletePoster(id);
  const reorder = useReorderPosters(id);
  const [pendingPoster, setPendingPoster] = useState<PendingPoster | null>(null);
  const [pendingDuration, setPendingDuration] = useState(10);
  const [durationTarget, setDurationTarget] = useState<{
    id: string;
    durationSec: number;
  } | null>(null);

  const methods = RHF.useForm<UpdateRoomDisplayScreenDTO>({
    resolver: zodResolver(UpdateRoomDisplayScreenSchema),
    defaultValues: {
      name: "",
      slug: "",
      isActive: true,
    },
  });

  // Reset only when server record changes — avoid wiping edits on re-render
  const screenRevision = detail.data
    ? `${detail.data.id}:${detail.data.updatedAt}`
    : "";

  useEffect(() => {
    if (!detail.data) return;
    methods.reset({
      name: detail.data.name,
      slug: detail.data.slug,
      isActive: detail.data.isActive,
    });
  }, [screenRevision]);

  if (detail.isLoading) {
    return (
      <Main>
        <p className="text-muted-foreground">ກຳລັງໂຫຼດ…</p>
      </Main>
    );
  }

  if (!detail.data) {
    return (
      <Main>
        <p className="text-muted-foreground">ບໍ່ພົບຂໍ້ມູນ</p>
        <Button
          className="mt-3"
          variant="outline"
          onClick={() => nav({ to: "/app/room-displays" })}
        >
          ກັບຄືນ
        </Button>
      </Main>
    );
  }

  const screen = detail.data;
  const posters = screen.posters ?? [];

  const movePoster = (index: number, dir: -1 | 1) => {
    const next = index + dir;
    if (next < 0 || next >= posters.length) return;
    const ids = posters.map((p) => p.id);
    const tmp = ids[index]!;
    ids[index] = ids[next]!;
    ids[next] = tmp;
    toast.promise(reorder.mutateAsync(ids), {
      loading: "ກຳລັງຈັດລຳດັບ…",
      success: "ຈັດລຳດັບແລ້ວ",
      error: "ຈັດລຳດັບບໍ່ສຳເລັດ",
    });
  };

  return (
    <>
      <Header>
        <Breadcrumb
          items={[
            { label: "ແຜງຄວບຄຸມ", to: "/app/dashboard" },
            { label: "ຈໍສະແດງຜົນ", to: "/app/room-displays" },
            { label: screen.name },
          ]}
        />
      </Header>
      <Main>
        <div className="mb-6 flex flex-wrap items-start justify-between gap-3">
          <div>
            <h1 className="font-semibold text-xl">{screen.name}</h1>
            <p className="font-mono text-muted-foreground text-sm">
              /display/{screen.slug}
            </p>
          </div>
          <Button
            variant="outline"
            onClick={() =>
              window.open(`/display/${screen.slug}`, "_blank", "noopener")
            }
          >
            <ExternalLinkIcon className="size-4" />
            ເປີດຈໍສະແດງຜົນ
          </Button>
        </div>

        <div className="grid gap-6 lg:grid-cols-2">
          <section className="rounded-xl border bg-card p-6">
            <h2 className="mb-4 font-medium">ຕັ້ງຄ່າຈໍ</h2>
            <FormRoot
              methods={methods}
              onSubmit={async (values) => {
                try {
                  await updateScreen.mutateAsync({
                    ...values,
                    slug: values.slug?.trim().toLowerCase(),
                  });
                  toast.success("ບັນທຶກແລ້ວ");
                } catch (e) {
                  toast.error(e instanceof Error ? e.message : "ບັນທຶກບໍ່ສຳເລັດ");
                }
              }}
              className="flex flex-col gap-4"
            >
              <FormInput name="name" label="ຊື່" requiredMark />
              <FormInput name="slug" label="Slug" requiredMark />
              <FormCheckbox name="isActive" label="ເປີດໃຊ້" />
              {canUpdate ? (
                <Button type="submit" disabled={updateScreen.isPending}>
                  ບັນທຶກ
                </Button>
              ) : null}
            </FormRoot>
          </section>

          <section className="rounded-xl border bg-card p-6">
            <h2 className="mb-4 font-medium">Poster</h2>

            {canUpdate ? (
              <div className="mb-4 space-y-4 rounded-lg border border-dashed p-4">
                <RoomDisplayImageUpload
                  screenId={id}
                  onSuccess={(key) =>
                    setPendingPoster({ kind: "image", key, durationSec: 10 })
                  }
                />
                <RoomDisplayVideoUpload
                  screenId={id}
                  onSuccess={({ key, videoDurationSec }) =>
                    setPendingPoster({
                      kind: "video",
                      key,
                      videoDurationSec,
                    })
                  }
                />
                {pendingPoster?.kind === "image" ? (
                  <div className="flex flex-wrap items-end gap-3">
                    <div className="space-y-1">
                      <label className="font-medium text-sm">
                        ເວລາສະແດງ (ວິນາທີ)
                      </label>
                      <Input
                        type="number"
                        min={5}
                        max={120}
                        value={pendingDuration}
                        onChange={(e) =>
                          setPendingDuration(Number(e.target.value) || 10)
                        }
                        className="w-28"
                      />
                    </div>
                    <Button
                      disabled={createPoster.isPending}
                      onClick={async () => {
                        try {
                          await toast.promise(
                            createPoster.mutateAsync({
                              imageKey: pendingPoster.key,
                              mediaType: "image",
                              durationSec: Math.min(
                                120,
                                Math.max(5, pendingDuration),
                              ),
                            }),
                            {
                              loading: "ກຳລັງບັນທຶກ…",
                              success: "ເພີ່ມ poster ແລ້ວ",
                              error: "ເພີ່ມ poster ບໍ່ສຳເລັດ",
                            },
                          );
                          setPendingPoster(null);
                          setPendingDuration(10);
                        } catch {
                          // toast already shown
                        }
                      }}
                    >
                      ເພີ່ມຮູບໃສ່ຈໍ
                    </Button>
                  </div>
                ) : null}
                {pendingPoster?.kind === "video" ? (
                  <div className="flex flex-wrap items-center gap-3">
                    <p className="text-muted-foreground text-sm">
                      ວິດີໂອ {pendingPoster.videoDurationSec.toFixed(1)}s — ຈະຫຼິ້ນຈົນຈົບ
                    </p>
                    <Button
                      disabled={createPoster.isPending}
                      onClick={async () => {
                        try {
                          await toast.promise(
                            createPoster.mutateAsync({
                              imageKey: pendingPoster.key,
                              mediaType: "video",
                              videoDurationSec: pendingPoster.videoDurationSec,
                            }),
                            {
                              loading: "ກຳລັງບັນທຶກ…",
                              success: "ເພີ່ມວິດີໂອແລ້ວ",
                              error: "ເພີ່ມວິດີໂອບໍ່ສຳເລັດ",
                            },
                          );
                          setPendingPoster(null);
                        } catch {
                          // toast already shown
                        }
                      }}
                    >
                      ເພີ່ມວິດີໂອໃສ່ຈໍ
                    </Button>
                  </div>
                ) : null}
              </div>
            ) : null}

            {posters.length === 0 ? (
              <p className="text-muted-foreground text-sm">ຍັງບໍ່ມີ poster</p>
            ) : (
              <ul className="flex flex-col gap-3">
                {posters.map((poster, index) => (
                  <li
                    key={poster.id}
                    className="flex gap-3 rounded-lg border p-3"
                  >
                    <div className="h-20 w-28 shrink-0 overflow-hidden rounded bg-neutral-100">
                      {poster.imageUrl ? (
                        poster.mediaType === "video" ? (
                          <video
                            src={poster.imageUrl}
                            muted
                            playsInline
                            className="h-full w-full object-cover"
                          />
                        ) : (
                          <img
                            src={poster.imageUrl}
                            alt=""
                            className="h-full w-full object-cover"
                          />
                        )
                      ) : null}
                    </div>
                    <div className="min-w-0 flex-1 space-y-2">
                      <div className="flex flex-wrap items-center gap-2">
                        <Badge
                          variant={poster.isActive ? "default" : "secondary"}
                        >
                          {poster.isActive ? "ເປີດໃຊ້" : "ປິດ"}
                        </Badge>
                        <Badge variant="outline">
                          {poster.mediaType === "video" ? "ວິດີໂອ" : "ຮູບ"}
                        </Badge>
                        {poster.mediaType === "image" ? (
                          <span className="text-muted-foreground text-sm">
                            {poster.durationSec}s
                          </span>
                        ) : (
                          <span className="text-muted-foreground text-sm">
                            ຫຼິ້ນຈົນຈົບ
                          </span>
                        )}
                      </div>
                      {canUpdate ? (
                        <div className="flex flex-wrap gap-2">
                          <Button
                            type="button"
                            size="sm"
                            variant="outline"
                            onClick={() => movePoster(index, -1)}
                            disabled={index === 0 || reorder.isPending}
                          >
                            <ArrowUpIcon className="size-3.5" />
                          </Button>
                          <Button
                            type="button"
                            size="sm"
                            variant="outline"
                            onClick={() => movePoster(index, 1)}
                            disabled={
                              index === posters.length - 1 || reorder.isPending
                            }
                          >
                            <ArrowDownIcon className="size-3.5" />
                          </Button>
                          <Button
                            type="button"
                            size="sm"
                            variant="outline"
                            onClick={() =>
                              toast.promise(
                                updatePoster.mutateAsync({
                                  posterId: poster.id,
                                  input: { isActive: !poster.isActive },
                                }),
                                {
                                  loading: "ກຳລັງອັບເດດ…",
                                  success: "ອັບເດດແລ້ວ",
                                  error: "ອັບເດດບໍ່ສຳເລັດ",
                                },
                              )
                            }
                          >
                            {poster.isActive ? "ປິດ" : "ເປີດໃຊ້"}
                          </Button>
                          <Button
                            type="button"
                            size="sm"
                            variant="outline"
                            disabled={poster.mediaType === "video"}
                            onClick={() =>
                              setDurationTarget({
                                id: poster.id,
                                durationSec: poster.durationSec,
                              })
                            }
                          >
                            ເວລາສະແດງ
                          </Button>
                          <Button
                            type="button"
                            size="sm"
                            variant="outline"
                            onClick={async () => {
                              const ok = await confirm({
                                title: "ລຶບ poster",
                                description: "ທ່ານແນ່ໃຈບໍ່ວ່າຈະລຶບ poster ນີ້?",
                                actionText: "ລຶບ",
                                ActionProps: {
                                  variant: "destructive",
                                },
                              });
                              if (!ok) return;
                              toast.promise(
                                deletePoster.mutateAsync(poster.id),
                                {
                                  loading: "ກຳລັງລຶບ…",
                                  success: "ລຶບແລ້ວ",
                                  error: "ລຶບບໍ່ສຳເລັດ",
                                },
                              );
                            }}
                          >
                            <Trash2Icon className="size-3.5" />
                          </Button>
                        </div>
                      ) : null}
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </section>
        </div>

        <EditPosterDurationDialog
          open={durationTarget != null}
          onOpenChange={(open) => {
            if (!open) setDurationTarget(null);
          }}
          durationSec={durationTarget?.durationSec ?? 10}
          submitting={updatePoster.isPending}
          onSubmit={async (durationSec) => {
            if (!durationTarget) return;
            await toast.promise(
              updatePoster.mutateAsync({
                posterId: durationTarget.id,
                input: { durationSec },
              }),
              {
                loading: "ກຳລັງອັບເດດ…",
                success: "ອັບເດດແລ້ວ",
                error: "ອັບເດດບໍ່ສຳເລັດ",
              },
            );
            setDurationTarget(null);
          }}
        />
      </Main>
    </>
  );
}
